import { apiInstance } from '@/api/instance';

/**
 * Contract: bodyprint-api/docs/external-apis/partners-contract.md (Seus links) and docs/admin/acquisition.md. The
 * partner only reads aggregates: never who signed up, never revenue or cost.
 */
type AcquisitionStage =
  | 'signed_up'
  | 'organization_created'
  | 'trial_started'
  | 'first_athlete_joined'
  | 'paying'
  | 'joined_organization'
  | 'first_train_completed';

/** Counts of a period; stages count people who signed up in it (cohort), up to now. */
export interface AcquisitionNumbers {
  clicks: number;
  /** Clicks by distinct device, without bots. */
  uniqueClicks: number;
  stages: Record<AcquisitionStage, number>;
}

interface AcquisitionLinkNumbers extends AcquisitionNumbers {
  key: string;
  label: string;
  channel: string;
  campaignId: string | null;
  linkId: string | null;
}

/** Archived links never come back. */
type AcquisitionLinkStatus = 'active' | 'paused';

/** What the link's coupon grants, once per person. */
export type AcquisitionBenefit = { kind: 'trial_days'; value: number };

export interface PartnerAcquisitionLink {
  id: string;
  code: string;
  label: string;
  status: AcquisitionLinkStatus;
  couponCode: string | null;
  benefit: AcquisitionBenefit | null;
  createdAt: string;
  campaign: { name: string; startsAt: string | null; endsAt: string | null } | null;
  /** `https://bodyprint.com.br/c/<code>`. */
  shortUrl: string;
  /** `null` when the link brought nothing in the period. */
  numbers: AcquisitionLinkNumbers | null;
}

/** One São Paulo day of the period; the API fills days without activity with zeros. */
export interface AcquisitionSeriesPoint {
  day: string;
  clicks: number;
  signups: number;
  paying: number;
}

export interface PartnerAcquisitionCommissions {
  accruedCents: number;
  approvedCents: number;
  paidCents: number;
}

export interface PartnerAcquisitionResponse {
  period: { from: string; to: string; model: string; modelLabel: string; audience: string };
  totals: AcquisitionNumbers;
  series: AcquisitionSeriesPoint[];
  links: PartnerAcquisitionLink[];
  commissions: PartnerAcquisitionCommissions;
}

/** São Paulo days, `YYYY-MM-DD`, `to` inclusive. Without them the API reads the last 30 days. */
export interface PartnerAcquisitionParams {
  from?: string;
  to?: string;
}

export const partnersAcquisitionApi = {
  read: (params: PartnerAcquisitionParams) =>
    apiInstance.get<PartnerAcquisitionResponse>('partners/acquisition', { params }),
};

export const partnersAcquisitionApiKeys = {
  all: ['acquisition'] as const,
  read: (params: PartnerAcquisitionParams) => ['acquisition', params] as const,
};
